/**
 * Pure parsing functions for converting backend tokens to frontend placeholders.
 *
 * All functions in this file are pure (no side effects, no mutations).
 * They take input state and return new state objects.
 *
 * Protocol:
 * - Facts: Backend sends ^fid={factId}^
 *   - On opening ^, emit [[FACT_LOADING]] and buffer until closing ^
 *   - On closing ^, replace [[FACT_LOADING]] with [[FACT:{factId}]]
 *
 * - Tables: Backend sends ```[lang]\n...\n```
 *   - On opening ```, emit [[TABLE_LOADING:id]] and buffer until closing ```
 *   - On closing ```, replace [[TABLE_LOADING:id]] with [[TABLE:id:encodedContent]]
 *   - Only content containing | is treated as a table; otherwise emitted as plain text
 */

import type { ParserState } from './types';

// Counter for generating unique table block IDs
let tableBlockIdCounter = 0;

/**
 * Generate a unique ID for a table block.
 */
function generateTableBlockId(): string {
  return `tbl-${Date.now()}-${tableBlockIdCounter++}`;
}

/**
 * Create a new parser state with initial values.
 *
 * @returns A fresh parser state ready to process characters
 */
export function createParserState(): ParserState {
  return {
    buffer: '',
    pendingFact: false,
    factBuffer: '',
    rendered: '',
    // Table fields
    pendingTable: false,
    tableBuffer: '',
    tableBlockId: null,
    backtickBuffer: '',
    tableLang: '',
    tableLangConsumed: false,
  };
}

/**
 * Check if a character is a fact delimiter (opening or closing).
 */
export function isFactDelimiter(char: string): boolean {
  return char === '^';
}

/**
 * Extract the fact ID from a fact buffer string.
 */
export function extractFactId(factBuffer: string): string | null {
  if (factBuffer.startsWith('fid=')) {
    const factId = factBuffer.slice(4);
    return factId || null;
  }
  return factBuffer || null;
}

/**
 * Check if content looks like a markdown table (contains pipe character).
 */
function isTableContent(content: string): boolean {
  return content.includes('|');
}

/**
 * Handle a backtick character - potentially starting or ending a code block.
 * Returns the new state after processing the backtick.
 */
function handleBacktick(state: ParserState): ParserState {
  const newBacktickBuffer = state.backtickBuffer + '`';

  // Check if we've accumulated three backticks
  if (newBacktickBuffer === '```') {
    if (!state.pendingTable) {
      // Opening a code block - emit loading placeholder and enter table mode
      const blockId = generateTableBlockId();
      return {
        ...state,
        pendingTable: true,
        tableBuffer: '',
        tableBlockId: blockId,
        backtickBuffer: '',
        tableLang: '',
        tableLangConsumed: false,
        rendered: state.rendered + `[[TABLE_LOADING:${blockId}]]`,
      };
    } else {
      // Closing a code block - finalize the table
      const content = state.tableBuffer;
      const blockId = state.tableBlockId;

      if (isTableContent(content)) {
        // Content has pipes - treat as table
        const encodedContent = encodeURIComponent(content);
        const placeholder = `[[TABLE:${blockId}:${encodedContent}]]`;
        const newRendered = state.rendered.replace(
          `[[TABLE_LOADING:${blockId}]]`,
          placeholder
        );
        return {
          ...state,
          pendingTable: false,
          tableBuffer: '',
          tableBlockId: null,
          backtickBuffer: '',
          tableLang: '',
          tableLangConsumed: false,
          rendered: newRendered,
        };
      } else {
        // No pipes - emit as plain text code block
        const langPrefix = state.tableLang ? state.tableLang + '\n' : '';
        const codeBlock = '```' + langPrefix + content + '```';
        const newRendered = state.rendered.replace(
          `[[TABLE_LOADING:${blockId}]]`,
          codeBlock
        );
        return {
          ...state,
          pendingTable: false,
          tableBuffer: '',
          tableBlockId: null,
          backtickBuffer: '',
          tableLang: '',
          tableLangConsumed: false,
          rendered: newRendered,
        };
      }
    }
  }

  // Still accumulating backticks (1 or 2 so far)
  return {
    ...state,
    backtickBuffer: newBacktickBuffer,
  };
}

/**
 * Flush any pending backticks that didn't form a triple-backtick sequence.
 * Called when we encounter a non-backtick character while backtickBuffer is non-empty.
 */
function flushBacktickBuffer(state: ParserState): ParserState {
  if (!state.backtickBuffer) {
    return state;
  }

  if (state.pendingTable) {
    // Inside a table - add backticks to table buffer
    return {
      ...state,
      tableBuffer: state.tableBuffer + state.backtickBuffer,
      backtickBuffer: '',
    };
  } else {
    // Outside table - add backticks to rendered output
    return {
      ...state,
      rendered: state.rendered + state.backtickBuffer,
      backtickBuffer: '',
    };
  }
}

/**
 * Handle a character while inside a table code block.
 */
function handleTableCharacter(char: string, state: ParserState): ParserState {
  // If we haven't consumed the language line yet, look for newline
  if (!state.tableLangConsumed) {
    if (char === '\n') {
      // Language line complete
      return {
        ...state,
        tableLangConsumed: true,
      };
    } else {
      // Accumulate language identifier
      return {
        ...state,
        tableLang: state.tableLang + char,
      };
    }
  }

  // Regular table content - add to buffer
  return {
    ...state,
    tableBuffer: state.tableBuffer + char,
  };
}

/**
 * Parse a single character and update the parser state immutably.
 *
 * This is the core parsing function that processes one character at a time
 * and returns a new parser state. It handles:
 * - Fact tokens: ^fid=xxx^
 * - Table code blocks: ```...```
 * - Normal text characters
 *
 * @param char - The character to parse
 * @param state - The current parser state
 * @returns A new parser state with the character processed
 */
export function parseCharacter(char: string, state: ParserState): ParserState {
  // Handle backtick - potentially table delimiter
  if (char === '`') {
    return handleBacktick(state);
  }

  // If we have pending backticks that didn't form ```, flush them first
  if (state.backtickBuffer) {
    state = flushBacktickBuffer(state);
  }

  // If we're inside a table code block, buffer the character
  if (state.pendingTable) {
    return handleTableCharacter(char, state);
  }

  // Handle caret delimiter - opening or closing a fact token
  if (isFactDelimiter(char)) {
    if (!state.pendingFact) {
      // Opening a fact token - emit loading placeholder
      return {
        ...state,
        pendingFact: true,
        factBuffer: '',
        rendered: state.rendered + '[[FACT_LOADING]]',
      };
    }

    // Closing a fact token - replace loading spinner with actual fact
    const factId = extractFactId(state.factBuffer);

    if (factId) {
      // Valid fact ID - replace first [[FACT_LOADING]] with [[FACT:{factId}]]
      const newRendered = state.rendered.replace('[[FACT_LOADING]]', `[[FACT:${factId}]]`);
      return {
        ...state,
        pendingFact: false,
        factBuffer: '',
        rendered: newRendered,
      };
    } else {
      // Invalid fact token - replace spinner with literal text
      const literalText = `^${state.factBuffer}^`;
      const newRendered = state.rendered.replace('[[FACT_LOADING]]', literalText);
      return {
        ...state,
        pendingFact: false,
        factBuffer: '',
        rendered: newRendered,
      };
    }
  }

  // Inside fact token - accumulate characters in factBuffer
  if (state.pendingFact) {
    return {
      ...state,
      factBuffer: state.factBuffer + char,
    };
  }

  // Normal text - append to rendered output
  return {
    ...state,
    rendered: state.rendered + char,
  };
}

/**
 * Parse multiple characters at once.
 *
 * This is a convenience function that processes a string of characters
 * by calling parseCharacter for each one.
 *
 * @param text - The text to parse
 * @param state - The current parser state
 * @returns A new parser state with all characters processed
 */
export function parseText(text: string, state: ParserState): ParserState {
  let currentState = state;
  for (const char of text) {
    currentState = parseCharacter(char, currentState);
  }
  return currentState;
}

/**
 * Get the current rendered output from the parser state.
 */
export function getRenderedText(state: ParserState): string {
  return state.rendered;
}

/**
 * Check if the parser is currently inside a fact token.
 */
export function isPendingFact(state: ParserState): boolean {
  return state.pendingFact;
}

/**
 * Check if the parser is currently inside a table code block.
 */
export function isPendingTable(state: ParserState): boolean {
  return state.pendingTable;
}
